import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import type { ApiContact } from '@/lib/apiTypes'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface UseContactSearchOptions {
  /** The raw search query. Leading/trailing whitespace is preserved for debounce; the request uses the trimmed value. */
  query: string
  /** Minimum characters required before a request is fired. Default: 2. */
  minChars?: number
  /** Page size sent to the API. Default: 8. */
  take?: number
  /** Debounce delay in ms. Default: 300. */
  debounceMs?: number
}

export interface UseContactSearchResult {
  results: ApiContact[]
  loading: boolean
  error: Error | null
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Pure data hook for contact typeahead search.
 *
 * - Debounces the query (default 300 ms).
 * - Returns no results until `query` reaches `minChars` characters.
 * - Backed by TanStack Query so results participate in the global cache and
 *   can be invalidated by mutations elsewhere in the app.
 */
export function useContactSearch({
  query,
  minChars = 2,
  take = 8,
  debounceMs = 300,
}: UseContactSearchOptions): UseContactSearchResult {
  const [debouncedQuery, setDebouncedQuery] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query)
    }, debounceMs)
    return () => clearTimeout(timer)
  }, [query, debounceMs])

  const trimmed = debouncedQuery.trim()
  const enabled = trimmed.length >= minChars

  const result = useQuery<ApiContact[], Error>({
    queryKey: ['contacts', 'search', trimmed, take],
    queryFn: async () => {
      const res = await apiClient.get('/api/contacts', { params: { search: trimmed, take } })
      const items: ApiContact[] = res.data?.data?.items ?? res.data?.items ?? []
      return items
    },
    enabled,
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  })

  return {
    results: enabled ? (result.data ?? []) : [],
    loading: enabled && result.isFetching,
    error: result.error ?? null,
  }
}
